import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../services/supabase';
import { inviteService } from '../../services/invites';

const AuthRedirectHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hasChecked, setHasChecked] = useState(false);

  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // console.log('Auth state changed:', event, session);
      
      if (event === 'PASSWORD_RECOVERY') {
        // console.log('Password recovery event detected!');
        navigate('/reset-password', { replace: true });
      }
    });
    
    return () => subscription.unsubscribe();
  }, [navigate]);
  
  useEffect(() => {
    if (hasChecked) return;
    
    const checkAuthAndRedirect = async () => {
      // Check both query parameters and hash fragments
      let params = new URLSearchParams(location.search);
      
      // Also check hash fragment (Supabase sometimes uses this)
      if (window.location.hash) {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        // Merge hash params with query params
        for (const [key, value] of hashParams) {
          if (!params.has(key)) {
            params.set(key, value);
          }
        }
      }
      
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      const type = params.get('type');
      const error = params.get('error');
      const errorDescription = params.get('error_description');
      
      // Check if user just came from a password reset email
      const { data: { session } } = await supabase.auth.getSession();
      const isPasswordReset = session?.user?.app_metadata?.provider === 'email' && 
                             session?.user?.user_metadata?.email_verified === true &&
                             session?.user?.recovery_sent_at;
      
      // Debug logging
      // console.log('AuthRedirectHandler - Full URL:', window.location.href);
      // console.log('AuthRedirectHandler - Hash:', window.location.hash);
      // console.log('AuthRedirectHandler - Search:', location.search);
      // console.log('AuthRedirectHandler - Parsed params:', { accessToken, refreshToken, type, error, errorDescription });
      // console.log('AuthRedirectHandler - Session:', session);
      // console.log('AuthRedirectHandler - Is Password Reset?', isPasswordReset);

      // Handle password reset flow - check multiple conditions
      if ((type === 'recovery' && accessToken) || isPasswordReset) {
        // console.log('AuthRedirectHandler - Password reset detected, redirecting...');
        // Build query string with the parameters
        const resetParams = new URLSearchParams();
        if (accessToken) resetParams.set('access_token', accessToken);
        if (refreshToken) resetParams.set('refresh_token', refreshToken);
        if (type) resetParams.set('type', type);
        
        // Redirect to reset password page
        navigate('/reset-password?' + resetParams.toString(), { replace: true });
        setHasChecked(true);
        return;
      }

      // Handle authentication errors
      if (error) {
        console.error('Auth error:', error, errorDescription);
        navigate('/login?error=' + encodeURIComponent(errorDescription || error), { replace: true });
        setHasChecked(true);
        return;
      }

      // Handle sign-up confirmation
      if (type === 'signup') {
        // Check if there's a pending invite to accept
        const pendingInviteToken = localStorage.getItem('pending_invite_token');
        if (pendingInviteToken) {
          try {
            await inviteService.acceptInvite(pendingInviteToken);
            localStorage.removeItem('pending_invite_token');
            navigate('/login?message=Email confirmed and invitation accepted. You can now log in.', { replace: true });
          } catch (err) {
            console.error('Error accepting invite:', err);
            localStorage.removeItem('pending_invite_token');
            navigate('/login?message=Email confirmed. You can now log in.', { replace: true });
          }
        } else {
          navigate('/login?message=Email confirmed. You can now log in.', { replace: true });
        }
        setHasChecked(true);
        return;
      }
      
      setHasChecked(true);
    };
    
    checkAuthAndRedirect();
  }, [navigate, location.search, location.pathname, hasChecked]);

  return null;
};

export default AuthRedirectHandler;
